import {
  parsePhoneNumberFromString as parseMin,
  isSupportedCountry,
  parsePhoneNumber,
} from 'libphonenumber-js';
import { parsePhoneNumberFromString as parseMobile } from 'libphonenumber-js/mobile';
import * as lib from '../dependencies';
import log from './log';

export function formatNumber(number, type) {
  if (number && type) {
    if (type === 'formatInt') {
      if (number.length > 3 && String(number).slice(0, 2) === '00') {
        const analyzer = number.split('');
        if (analyzer[2] === '0') {
          return number;
        }
        const formatedNumber = parseMin(String(`+${number.slice(2)}`));
        if (formatedNumber) {
          return formatedNumber.formatInternational();
        }
        // return false;
      }
      return number;
    }
    if (type === 'countryCode') {
      if (number.length > 3) {
        if (String(number).slice(0, 2) === '00') {
          const analyzer = number.split('');
          if (analyzer[2] === '0') {
            return false;
          }
          const formatedCountryCallingCode = parseMin(
            String(`+${number.slice(2)}`)
          );
          if (formatedCountryCallingCode) {
            return formatedCountryCallingCode.countryCallingCode;
          }
          return false;
        }

        if (String(number).slice(0, 1) === '+') {
          const formatedCode = parseMin(String(number));
          if (formatedCode) {
            return formatedCode.countryCallingCode;
          }

          return false;
        }
      }
      return false;
    }
  }
  return false;
}

/**
 * Convert phone numbers into national format.
 * @param {String} phoneNumber
 * @param {String} countryCode
 * @returns a national number or null for invalid number and param type.
 */
export function parseNationalNumber(phoneNumber, countryCode = 'IE') {
  if (
    typeof phoneNumber !== 'string' ||
    !phoneNumber?.length ||
    !isSupportedCountry(countryCode)
  ) {
    return null;
  }

  let number = null;

  if (phoneNumber.slice(0, 1) === '0' && phoneNumber.slice(0, 2) !== '00') {
    number = phoneNumber.substring(1);
  } else {
    number = phoneNumber;
  }

  const numberParsed = parseMin(number, countryCode);

  return numberParsed?.nationalNumber ? numberParsed.nationalNumber : null;
}

/**
 * Check if the text has invalid GSM 7-bit alphabet characters.
 * @param {String} text
 * @returns an array with the invalid characters or an empty list
 */
export function checkGSMAlphabet(text) {
  let matches = text.match(
    // eslint-disable-next-line no-useless-escape
    /[^@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ`a-zäöñüà^{}\\\[~\]|€]/g
  );

  if (matches) {
    // Avoid duplication
    matches = matches.filter(
      (value, index, self) => self.indexOf(value) === index
    );
  }

  return matches || [];
}

/**
 * Returns the number of possible break lines for a given text.
 */
export function countLineBreaks(text, maxLen = 19) {
  if (!text) return 0;

  const chunks = text.split('\n'); // honor manual line breaks
  let count = 0;

  chunks.forEach(chunk => {
    count += Math.ceil(chunk.length / maxLen) || 1;
  });

  return count;
}

export function normalizeString(text) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function validateNumberType(mobile, countryCode) {
  let mobileNumber = String(mobile);

  if (mobileNumber.slice(0, 2) === '00') {
    const num = mobileNumber.slice(2, mobileNumber.length);
    mobileNumber = parseMobile('+'.concat(num));
  } else if (mobileNumber.slice(0, 1) === '+') {
    mobileNumber = parseMobile(mobile);
  } else {
    mobileNumber = parseMobile(mobile, countryCode);
  }

  if (mobileNumber && mobileNumber.isValid()) {
    return mobileNumber.getType();
  }
  return undefined;
}

export function isMobileNumber(mobile, countryCode) {
  return validateNumberType(mobile, countryCode) === 'MOBILE';
}

export function isUSNumber(number) {
  try {
    if (number === undefined || number === null) return false;
    if (typeof number !== 'string' && typeof number !== 'number') return false;

    const numStr = String(number);

    // Check if it is extension
    if (numStr.length < 5) return false;

    const phoneNumber = parsePhoneNumber(numStr);
    if (phoneNumber.country === 'US') return true;
  } catch (error) {
    log.error('Failed to validate the U.S. phone number.', error);
  }
  return false;
}

export function strcmp(str1, str2) {
  return str1.trim().localeCompare(str2, { sensitivity: 'base' }) === 0
    ? false
    : str1;
}

// This method return hours, minutes, seconds formatted for calllogs
export function dateParse(duration, call_date) {
  const dateTime = new Date(null);
  // eslint-disable-next-line no-restricted-globals
  dateTime.setSeconds(isNaN(duration) ? 0 : duration); // specify value for SECONDS here
  const ISOString = dateTime
    .toISOString()
    .substr(11, 8)
    .split(':');

  const isNotZero = value => value !== '00';

  const hours = isNotZero(ISOString[0])
    ? `${ISOString[0].replace(/^0+/, '')}h `
    : '';

  const minutes = isNotZero(ISOString[1])
    ? `${ISOString[1].replace(/^0+/, '')}m `
    : '';

  const seconds = isNotZero(ISOString[2])
    ? `${ISOString[2].replace(/^0+/, '')}s `
    : '';

  const timeParsed = call_date?.split(' ')[1].split(':') || '';

  return {
    hours,
    minutes,
    seconds,
    timeParsed,
  };
}

/**
 * This method return calllog line header title
 * @param {string} callDate eg. 2022-02-09 15:51:14
 * @returns Today Wed 09 February or Wed 09 February or exception for invalid arguments
 */
export function dateParseComplete(callDate) {
  if (!lib._.isString(callDate)) {
    throw Error('Invalid call date argument type.');
  }

  const today = new Date();
  const dateNow = lib.dayjs(today).format('DD MM YYYY');
  const date = callDate.split(' ').shift(1);

  if (lib.dayjs(date).format('DD MM YYYY') === 'Invalid Date') {
    throw Error('Invalid call date argument type.');
  }

  if (lib.dayjs(date).format('DD MM YYYY') === dateNow) {
    return `Today ${lib.dayjs(date).format('DD MMMM')}`;
  }
  return `${lib.dayjs(date).format('ddd DD MMMM')}`;
}

export function validateAttr(attr) {
  if (attr !== null && attr !== '') {
    return attr;
  }
  return null;
}

// This method return a string like continue string...
export function truncate(str, n) {
  if (!str) {
    return '';
  }
  return str.length > n ? `${str.substr(0, n - 1)}...` : str;
}

// This method return a string like continue string..., if the string has space, so returns before space string first
export function truncateBeforeSpaceFirst(str, n) {
  if (!str) {
    return '';
  }

  if (typeof str === 'string' && str.includes(' ')) {
    return str.split(' ')[0];
  }

  return str.length > n ? `${str.substr(0, n - 1)}...` : str;
}

// This method return international number formatted
export function getInterNumber(number, countryCode) {
  if (number.startsWith('00' || number.startsWith('+'))) {
    return number;
  }
  return parseMin(number, countryCode).number;
}

// Extract the caller number from the SIP header.
export function extractSipCallerId(data) {
  if (data) {
    const reGlobal = /<sip:(.*?)@/g;
    const re = /<sip:(.*?)@/;
    const valueParsed = data.match(reGlobal);
    let remote;
    let callerName;

    // Find the caller name
    callerName = data.replace('->', '');
    callerName = callerName.replace(/ \([\s\S]*?\).*/g, '');
    callerName = callerName.replace(/ <[\s\S]*?>\.*/g, '');
    callerName = callerName.replace(/"/g, '');

    if (valueParsed?.length > 1) {
      remote = valueParsed[1].match(re);
      if (remote && remote[1]) {
        return {
          callerName: callerName?.trim(),
          callerNumber: remote[1]?.trim(),
        };
      }
    } else {
      remote = data.match(re);
      if (remote) {
        return {
          callerName: callerName?.trim(),
          callerNumber: remote[1]?.trim(),
        };
      }
    }
  }

  return null;
}

export function hasOnlyNumber(str) {
  if (str) {
    return /^\d+$/.test(str);
  }

  return false;
}

/**
 * @name getPackageVersion
 * @description Format the app version from the package.json
 * @param {string} userAgent
 * @param {string} version
 * @param {string} environment
 * @returns {string} app version
 */
export function getPackageVersion(userAgent, version, environment = null) {
  if (!userAgent || !version || !environment)
    throw new Error(
      'You must provide userAgent, version and environment variables.'
    );

  const desktopVersion = userAgent.match(/desktopVersion:(.*?)%/i) || '0.0';
  const mode = environment === 'development' ? 'd' : '';
  const versionWeb = version.split('.')[2];
  const hotfix = version.split('.')[3];
  const hotfixValue = hotfix ? `-f${hotfix}` : '';

  if (desktopVersion[1] === '1.2.1') {
    const ver = desktopVersion[1].split('.');
    desktopVersion[1] = `${ver[0]}.${ver[1]}`;
  }
  return `${desktopVersion[1]}.${versionWeb}${mode}${hotfixValue}`;
}

/**
 * @name formatUserAgent
 * @description Format the User-Agent
 * @param {string} userAgent
 * @param {string} version - The App version like 0.0.25.
 * @returns {string} Formated User-Agent
 */
export function formatUserAgent(userAgent, version) {
  if (!userAgent || !version)
    throw new Error('You should provide the userAgent and version.');

  const machineId = userAgent.match(/machineId:(.*?)%/i) || 'Unknown';
  const OS = userAgent.match(/OS:(.*?)%/i) || 'Unknown';
  const appVersion = getPackageVersion(
    userAgent,
    version,
    process.env.NODE_ENV
  );
  return `NUACOM-DESKTOP APP ${
    machineId !== 'Unknown' ? machineId[1] : machineId
  } ${OS !== 'Unknown' ? OS[1] : OS} ${appVersion && appVersion}`;
}

/**
 * @name formatCallerName
 * @description Format the caller name
 * @param {*} callerName
 * @returns {string}
 */
export function formatCallerName(callerName) {
  if (callerName) {
    const fmtCallerName = callerName.split('@');

    if (fmtCallerName.length > 0) {
      return fmtCallerName[0];
    }
  }

  return callerName;
}

export function getToken() {
  const name = 'token=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

/*
 * When contacts hasnt name assigned, the contact comes as number.
 * This funciton tries to modify to modify the number to international e.164
 */
export function formatInternationalE164(phoneNumber) {
  try {
    if (typeof phoneNumber !== 'string') return phoneNumber;
    // Check if it is extension
    if (phoneNumber.length < 5) return phoneNumber;

    // Check if phonenumber starts with "00" or "+"
    if (!phoneNumber.startsWith('00') && !phoneNumber.startsWith('+'))
      return phoneNumber;

    // Check if phoneNumber is NOT numeric
    if (!/^\d+$/.test(phoneNumber)) return phoneNumber;

    // Normalize the number to start with "+" if it starts with "00"
    const phoneNumberWithPlus = phoneNumber.startsWith('00')
      ? `+${phoneNumber.slice(2)}`
      : phoneNumber;

    return parsePhoneNumber(phoneNumberWithPlus).formatInternational();
  } catch (err) {
    log.error('Failed to format phone number as E.164: ', err);
    return phoneNumber;
  }
}

/**
 * @name formatInternationalNumber
 * @description Format International Number
 * @param {*} number
 * @returns {string}
 */
export function formatInternationalNumber(number) {
  try {
    if (!number || number.length < 5) return number;

    if (number.slice(0, 2) === '00' || number.slice(0, 1) === '+') {
      const subStr =
        number.slice(0, 2) === '00' ? `+${number.slice(2)}` : number;

      return parsePhoneNumber(subStr).formatInternational();
    }

    return number;
  } catch (err) {
    log.error('Failure to parse the international number. Reason: ', err);
    return number;
  }
}

/**
 * @name getInitials
 * @description Format the name initials.
 * @param {string} name
 * @returns {string}
 */
export function getInitials(name) {
  if (name && name.length > 1) {
    const nameData = name.split(' ');
    let initials = `${nameData[0][0]}${nameData[0][1]}`;

    if (nameData.length > 1) {
      initials = `${nameData[0][0]}${nameData[nameData.length - 1][0]}`;
    }

    return initials.toUpperCase();
  }

  return name?.toUpperCase() || '';
}

export function isUndefined(variable) {
  return variable === undefined || variable === null;
}

export function checkVideoAppVersion(version) {
  if (typeof version === 'string') {
    const electronVersion = version.split('.');

    if (electronVersion && electronVersion.length >= 2) {
      if (Number(electronVersion[1]) >= 25) {
        return true;
      }
    }
  }

  return false;
}
